/**
 * FORCED in-app APK self-update (sideloaded distribution — no Play Store).
 *
 * NATIVE shells only: reads the installed build number via @capacitor/app and
 * compares it with app_config.apk.versionCode (published by
 * scripts/release-apk.mjs). When behind, the whole app is REPLACED by a
 * centered "Update required" screen — dealers must update to continue.
 *
 * Download hand-off, newest first:
 *   1. @capacitor/browser custom tab (build ≥2 shells).
 *   2. window.open to the (external-origin) APK URL — Capacitor routes popup
 *      windows to the system browser, which downloads the APK. This is the
 *      same escape hatch the WhatsApp share fallback uses, so it is proven to
 *      work on first-generation shells.
 *
 * Because this code ships in the WEBSITE bundle, even old installed shells
 * gain the update gate automatically on their next launch.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useAppConfig } from '../hooks/useAppConfig';
import { isNative, isPlayBuild } from '../lib/platform';

async function installedBuildNumber(): Promise<number | null> {
  try {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    const n = Number(info.build);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

async function openDownload(url: string): Promise<void> {
  try {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url });
    return;
  } catch {
    /* plugin absent in first-generation shells — fall through */
  }
  // Popup windows are handed to the system browser by the Capacitor WebView.
  window.open(url, '_blank', 'noopener');
}

const PLAY_LISTING_URL =
  'https://play.google.com/store/apps/details?id=com.anywayitsolutions';

/** Blocking screen for OUTDATED PLAY installs: send them to the Play Store. */
function PlayUpdateScreen(): JSX.Element {
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex min-h-full flex-1 flex-col items-center justify-center bg-ground px-8 text-center">
      <div className="w-full max-w-sm rounded-[22px] bg-white px-6 py-8 shadow-card">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-light text-2xl">
          ▶
        </div>
        <h1 className="text-xl font-extrabold tracking-tight text-ink">Update from Play Store</h1>
        <p className="mt-2 text-[13.5px] leading-relaxed text-slate-500">
          This version can no longer sign in. Please update the app from the Play Store to
          continue — your account and approvals are safe.
        </p>
        <button
          type="button"
          className="mt-6 w-full rounded-full bg-brand py-3.5 text-[15px] font-bold text-white shadow-bar disabled:opacity-60"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void openDownload(PLAY_LISTING_URL).finally(() =>
              setTimeout(() => setBusy(false), 4000),
            );
          }}
        >
          {busy ? 'Opening Play Store…' : 'Open Play Store'}
        </button>
        <p className="mt-4 text-[11.5px] leading-relaxed text-slate-400">
          On the store page, tap <b>Update</b>. If no Update button shows yet, updates usually
          arrive automatically within a few hours.
        </p>
        <p className="mt-2 text-[11.5px] leading-relaxed text-slate-400">
          Still not seeing the update? Open your phone&apos;s <b>Settings → Apps → Google Play
          Store → Storage</b>, tap <b>Clear cache</b>, then reopen the Play Store and check
          again.
        </p>
      </div>
    </div>
  );
}

/** Wraps the app: renders the blocking update screen when the shell is old. */
export function ApkUpdateGate({ children }: { children: ReactNode }): JSX.Element {
  const config = useAppConfig();
  const [installed, setInstalled] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isNative) return;
    void installedBuildNumber().then(setInstalled);
  }, []);

  const apk = config?.apk;
  // Play builds never see the SIDELOAD gate — Google Play updates the app
  // itself, and Play policy forbids pushing users to an APK outside the store.
  // But a Play install older than apk.playVersionCode gets its own screen that
  // points AT the Play Store: auto-updates can lag by hours, and a stale build
  // whose login no longer works must explain itself instead of failing quietly.
  const playOutdated =
    isNative &&
    isPlayBuild &&
    typeof apk?.playVersionCode === 'number' &&
    installed !== null &&
    installed < apk.playVersionCode;
  const outdated =
    isNative && !isPlayBuild && !!apk && installed !== null && installed < apk.versionCode;

  if (playOutdated) return <PlayUpdateScreen />;
  if (!outdated || !apk) return <>{children}</>;

  return (
    <div className="flex min-h-full flex-1 flex-col items-center justify-center bg-ground px-8 text-center">
      <div className="w-full max-w-sm rounded-[22px] bg-white px-6 py-8 shadow-card">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-light text-2xl">
          ⬇️
        </div>
        <h1 className="text-xl font-extrabold tracking-tight text-ink">Update required</h1>
        <p className="mt-2 text-[13.5px] leading-relaxed text-slate-500">
          A new version of the app (v{apk.versionName}) is available. Please update to continue.
        </p>
        <button
          type="button"
          className="mt-6 w-full rounded-full bg-brand py-3.5 text-[15px] font-bold text-white shadow-bar disabled:opacity-60"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void openDownload(apk.url).finally(() =>
              setTimeout(() => setBusy(false), 4000),
            );
          }}
        >
          {busy ? 'Opening download…' : 'Update now'}
        </button>
        <p className="mt-4 text-[11.5px] leading-relaxed text-slate-400">
          The download opens in your browser. When it finishes, tap the file and choose
          <b> Update</b> — your login and data stay.
        </p>
        <p className="mt-2 text-[11px] text-slate-400">
          Trouble downloading? Open <b className="text-slate-500">anyway-apk.pages.dev</b> in
          Chrome.
        </p>
      </div>
    </div>
  );
}
